import type { ThemeVariant } from '../../contract.js';
import { defaultLight, defaultDark } from '../default/variants.js';

/**
 * Aurora family — northern lights theme.
 *
 * Chrome palette: teal / cyan, deliberately distinct from the brand
 * green so Aurora reads as its own family across every chrome
 * surface (CTA buttons, focus rings, links, card glows, popover
 * rings). The SHADER (shaders/aurora.ts AURORA_UNIFORMS) keeps its
 * emerald + magenta + teal "moment of charging" palette unchanged —
 * chrome and shader serve different roles and don't need to match
 * 1:1.
 *
 * Dark variant: cyan-500 (#06B6D4) accent on deep teal-tinted
 * near-black. Card glows + widget rings tinted to the same cyan so
 * the chrome reads as a coherent cool surface against the shader's
 * warm-hued aurora motion.
 *
 * Light variant: cyan-700 (#0E7490) accent on pale aqua-white. The
 * darker cyan is picked for the light variant so white onAccent
 * text has the contrast required for WCAG AA-normal compliance on
 * CTA labels. Contrast ratio ~5.0:1 (AA-normal pass + AA-large
 * pass). History: prior value was cyan-600 (#0891B2) at ~3.68:1
 * (AA-large only); the docstring of that earlier commit flagged the
 * cyan-700 bump as a follow-up — this commit actions that TODO.
 *
 * `charging` stays brand #21c163 on both variants per the
 * literal-brand-on-session-state convention every other theme
 * follows. `chargingGlow` keeps its magenta / pink halo from the
 * shader's secondary aurora colour (uLightColor2) so the charging-
 * state visual still echoes the aurora composition even after the
 * chrome palette swap.
 */
export const auroraLight: ThemeVariant = {
  mode: 'light',
  tokens: {
    ...defaultLight.tokens,
    colors: {
      ...defaultLight.tokens.colors,
      // Pale aqua-white bg — gives the pastel aurora shader and the
      // cyan chrome accents headroom without going pure white.
      bg: '#ECFEFF',
      // Surface (card) is the more-tinted teal-white; surface2
      // (raised card) is pure white. This treats surface2 as the
      // elevated layer, consistent with the dark variant's
      // surface < surface2 luminance order.
      surface: '#F0FDFF',
      surface2: '#FFFFFF',
      border: '#B8E0E8',
      // Stronger version of border — same hue, deeper shade so
      // borders that need presence (sticky CTAs, focus outlines on
      // pale surfaces) read clearly without flipping to accent.
      borderStrong: '#8FC2CB',
      // Cyan-700 accent — darker than the dark variant's cyan-500
      // because pale-bg light themes need more accent depth to
      // read, AND the extra depth lifts white onAccent contrast
      // past the WCAG AA-normal threshold (see header docstring).
      accent: '#0E7490',
      accentSoft: 'rgba(14, 116, 144, 0.14)',
      // Pure white text on the cyan-700 accent. WCAG ~5.0:1
      // (AA-normal pass + AA-large pass) — fully clears the bar for
      // CTA labels. Bumped from cyan-600 (3.68:1, AA-large only) in
      // this commit per the header-docstring TODO.
      onAccent: '#FFFFFF',
      charging: '#21c163',
      // Pastel pink halo on the charging-state glow — picks up the
      // secondary aurora colour from the shader so chrome and
      // canvas share the aurora vocabulary even after the cyan
      // chrome swap.
      chargingGlow: 'rgba(235, 178, 217, 0.45)',
      // Cool teal-slate UI mute — pairs with the cyan palette
      // without competing with text/textMute roles.
      mute: '#7E919A',
      // Deep slate-teal primary text. Strong contrast against the
      // pale aqua bg (~12:1).
      text: '#134E5E',
      // Cool slate secondary text — readable on bg/surface without
      // competing with primary text.
      textMute: '#5E7A85',
      // Cyan 3-stop card glow stack. Low alphas because the pale
      // aqua bg only needs a faint hue lift to read the glow. Hex
      // tracks the accent (cyan-700 #0E7490) for palette coherence;
      // the perceptual difference vs cyan-600 #0891B2 at these
      // alpha tints is imperceptible but keeps the design system
      // internally consistent (one accent, one glow hue).
      cardGlow:
        '0 0 0 1px color-mix(in srgb, #0E7490 14%, transparent),'
        + ' 0 0 18px -4px color-mix(in srgb, #0E7490 18%, transparent),'
        + ' 0 6px 20px -10px color-mix(in srgb, #0E7490 16%, transparent)',
      cardGlowSubtle:
        '0 0 0 1px color-mix(in srgb, #0E7490 7%, transparent),'
        + ' 0 0 10px -6px color-mix(in srgb, #0E7490 10%, transparent),'
        + ' 0 3px 10px -8px color-mix(in srgb, #0E7490 7%, transparent)',
      // Widget + chrome glass — pale aqua bg with cyan ring on the
      // shadow recipe. Reduced blur (matching defaultLight
      // convention) to keep the bright surface from reading as
      // muddy fog. Widget ring also tracks cyan-700.
      widgetBg: 'rgba(255, 255, 255, 0.82)',
      widgetBorder: 'rgba(19, 78, 94, 0.08)',
      widgetShadow:
        '0 8px 24px rgba(19, 78, 94, 0.10),'
        + ' 0 0 0 1px rgba(14, 116, 144, 0.12)',
      chromeBg: 'rgba(240, 253, 255, 0.94)',
      chromeBorder: 'rgba(19, 78, 94, 0.06)',
      chromeShadow: '0 4px 16px rgba(19, 78, 94, 0.10)',
    },
  },
};

export const auroraDark: ThemeVariant = {
  mode: 'dark',
  tokens: {
    ...defaultDark.tokens,
    colors: {
      ...defaultDark.tokens.colors,
      // Deep teal-tinted near-black bg — distinct from default's
      // pure near-black so the chrome reads as Aurora's own family
      // surface even on pages without a shader canvas (e.g.
      // /profile, /history).
      bg: '#0A1F26',
      surface: '#0E2A33',
      surface2: '#133842',
      border: '#1A4250',
      // Stronger version of border — same hue, lighter shade so
      // borders that need presence on raised surfaces read clearly
      // without flipping to accent.
      borderStrong: '#2E5F70',
      // Cyan-500 accent — the chrome accent for Aurora. Bright on
      // teal-near-black gives CTA pills strong presence without
      // competing with the shader canvas inside the card.
      accent: '#06B6D4',
      accentSoft: 'rgba(6, 182, 212, 0.16)',
      // Deep teal text on the cyan accent. WCAG ~6.73:1 (AA normal
      // + AAA large). Matches the variant's bg so CTA-text shares
      // the page-surface hue.
      onAccent: '#0A1F26',
      charging: '#21c163',
      // Magenta halo on the charging-state glow — picks up the
      // secondary aurora colour (uLightColor2) so chrome and
      // canvas share the aurora vocabulary even after the cyan
      // chrome swap.
      chargingGlow: 'rgba(235, 51, 179, 0.55)',
      // Deep teal-slate UI mute — cool, sits well against the
      // teal-tinted surface palette.
      mute: '#3A4855',
      // Pale cyan-white primary text. Strong contrast against the
      // teal-near-black bg.
      text: '#E0F2FE',
      // Cool slate-teal secondary text — readable on bg/surface
      // without competing with primary text.
      textMute: '#94A8B0',
      // Cyan 3-stop card glow stack. Same shape as Default dark /
      // Luce dark for visual consistency; only the hue swaps to
      // match the family identity.
      cardGlow:
        '0 0 0 1px color-mix(in srgb, #06B6D4 20%, transparent),'
        + ' 0 0 24px -4px color-mix(in srgb, #06B6D4 32%, transparent),'
        + ' 0 8px 32px -12px color-mix(in srgb, #06B6D4 28%, transparent)',
      cardGlowSubtle:
        '0 0 0 1px color-mix(in srgb, #06B6D4 10%, transparent),'
        + ' 0 0 14px -6px color-mix(in srgb, #06B6D4 16%, transparent),'
        + ' 0 4px 12px -8px color-mix(in srgb, #06B6D4 12%, transparent)',
      // Widget + chrome glass — teal-navy bg with cyan ring tint.
      // Heavy dense-glass blur matching defaultDark since popovers
      // sit over the dark surface here.
      widgetBg: 'rgba(14, 42, 51, 0.78)',
      widgetBorder: 'rgba(6, 182, 212, 0.12)',
      widgetShadow:
        '0 12px 32px rgba(0, 0, 0, 0.55),'
        + ' 0 0 0 1px rgba(6, 182, 212, 0.10)',
      chromeBg: 'rgba(14, 42, 51, 0.92)',
      chromeBorder: 'rgba(6, 182, 212, 0.10)',
      chromeShadow: '0 8px 24px rgba(0, 0, 0, 0.60)',
    },
  },
};
